import axios from '../utils/axios';
import { cacheUtils } from '../utils/performanceUtils';

/**
 * Base service class with standardized error handling, caching, and performance optimization
 */
class BaseService {
  constructor(baseEndpoint = '') {
    this.baseEndpoint = baseEndpoint;
    this.cache = cacheUtils.createCache(300000); // 5 minutes default TTL
  }

  /**
   * Handle API errors consistently across all services
   */
  handleError(error) {
    const message = error.response?.data?.message || error.message || 'An error occurred';
    const status = error.response?.status || 500;
    const errorCode = error.response?.data?.error_code || 'UNKNOWN_ERROR';
    const validationErrors = error.response?.data?.errors || null;

    console.error(`${this.constructor.name} API Error:`, {
      message,
      status,
      errorCode,
      url: error.config?.url,
      method: error.config?.method,
      timestamp: new Date().toISOString(),
      validationErrors,
    });

    // Handle specific error cases
    if (status === 401) {
      // Token might be expired, handled by axios interceptor
      return {
        success: false,
        error: 'Authentication required',
        status,
        errorCode: 'UNAUTHORIZED',
        shouldRedirect: true,
      };
    }

    if (status === 422 && validationErrors) {
      return {
        success: false,
        error: message,
        status,
        errorCode: 'VALIDATION_ERROR',
        validationErrors,
      };
    }

    return {
      success: false,
      error: message,
      status,
      errorCode,
    };
  }

  /**
   * Standard GET request with caching
   */
  async get(endpoint, params = {}, options = {}) {
    const { cache = true, cacheTTL = 300000 } = options;
    const cacheKey = this.getCacheKey('GET', endpoint, params);

    // Check cache first
    if (cache) {
      const cachedData = this.cache.get(cacheKey);
      if (cachedData) {
        return cachedData;
      }
    }

    try {
      // Add timestamp to prevent browser caching if cache is disabled
      const requestParams = { ...params };
      if (!cache) {
        requestParams._t = Date.now();
      }

      const response = await axios.get(`${this.baseEndpoint}${endpoint}`, { params: requestParams });
      const data = response.data;

      // Cache successful responses
      if (cache && data.success !== false) {
        this.cache.set(cacheKey, data, cacheTTL);
      }

      return data;
    } catch (error) {
      return this.handleError(error);
    }
  }

  /**
   * Standard POST request
   */
  async post(endpoint, data = {}) {
    try {
      const response = await axios.post(`${this.baseEndpoint}${endpoint}`, data);
      return response.data;
    } catch (error) {
      return this.handleError(error);
    }
  }

  /**
   * Standard PUT request
   */
  async put(endpoint, data = {}) {
    try {
      const response = await axios.put(`${this.baseEndpoint}${endpoint}`, data);
      return response.data;
    } catch (error) {
      return this.handleError(error);
    }
  }

  /**
   * Standard DELETE request
   */
  async delete(endpoint) {
    try {
      const response = await axios.delete(`${this.baseEndpoint}${endpoint}`);
      return response.data;
    } catch (error) {
      return this.handleError(error);
    }
  }

  /**
   * Validate required parameters
   */
  validateRequired(params, requiredFields) {
    const missing = requiredFields.filter(field => !params[field]);
    if (missing.length > 0) {
      throw new Error(`Missing required parameters: ${missing.join(', ')}`);
    }
  }

  /**
   * Format API response for consistent structure
   */
  formatResponse(data, success = true) {
    return {
      success,
      data,
      timestamp: new Date().toISOString(),
    };
  }

  /**
   * Generate cache key for requests
   */
  getCacheKey(method, endpoint, params = {}) {
    const paramString = Object.keys(params).length > 0 ? JSON.stringify(params) : '';
    return `${method}:${this.baseEndpoint}${endpoint}:${paramString}`;
  }

  /**
   * Clear cache for specific endpoint or all cache
   * @param {string|null} endpoint - The endpoint to clear cache for (e.g., '/schedules')
   * @param {string|null} method - Optional HTTP method to filter by (e.g., 'GET')
   * @returns {number} Number of cache entries cleared
   */
  clearCache(endpoint = null, method = null) {
    try {
      if (endpoint) {
        // Clear cache entries that match the endpoint
        const keys = Array.from(this.cache.keys ? this.cache.keys() : []);
        let clearedCount = 0;

        keys.forEach(key => {
          // Build the pattern to match: METHOD:BASEENDPOINT+ENDPOINT:
          const keyPattern = method
            ? `${method}:${this.baseEndpoint}${endpoint}:`
            : `${this.baseEndpoint}${endpoint}:`;

          if (key.startsWith(keyPattern)) {
            this.cache.delete(key);
            clearedCount++;
          }
        });

        if (clearedCount > 0) {
          console.debug(`[${this.constructor.name}] Cleared ${clearedCount} cache entries for endpoint: ${endpoint}`);
        }

        return clearedCount;
      } else {
        const previousSize = this.cache.size();
        this.cache.clear();
        console.debug(`[${this.constructor.name}] Cleared entire cache (${previousSize} entries)`);
        return previousSize;
      }
    } catch (error) {
      console.error(`[${this.constructor.name}] Error clearing cache:`, error);
      // Don't throw - cache clearing shouldn't break the application
      return 0;
    }
  }

  /**
   * Batch requests with automatic batching and caching
   */
  async batchGet(requests, options = {}) {
    const { batchSize = 5, cache = true } = options;
    const results = [];

    // Process requests in batches
    for (let i = 0; i < requests.length; i += batchSize) {
      const batch = requests.slice(i, i + batchSize);
      const batchPromises = batch.map(({ endpoint, params, options: reqOptions }) =>
        this.get(endpoint, params, { cache, ...reqOptions })
      );

      const batchResults = await Promise.allSettled(batchPromises);
      results.push(...batchResults.map(result =>
        result.status === 'fulfilled' ? result.value : { success: false, error: result.reason }
      ));
    }

    return results;
  }

  /**
   * Retry mechanism for failed requests
   */
  async withRetry(requestFunc, maxRetries = 3, delay = 1000) {
    let lastError;

    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        return await requestFunc();
      } catch (error) {
        lastError = error;

        // Don't retry on client errors (4xx)
        if (error.response?.status >= 400 && error.response?.status < 500) {
          throw error;
        }

        if (attempt < maxRetries) {
          await new Promise(resolve => setTimeout(resolve, delay * attempt));
        }
      }
    }

    throw lastError;
  }

  /**
   * Get cache statistics
   */
  getCacheStats() {
    return {
      size: this.cache.size(),
      hits: this.cacheHits || 0,
      misses: this.cacheMisses || 0,
      hitRate: this.cacheHits && this.cacheMisses
        ? ((this.cacheHits / (this.cacheHits + this.cacheMisses)) * 100).toFixed(2) + '%'
        : '0%',
    };
  }
}

export default BaseService;